import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Truck, AlertTriangle, RefreshCw, MessageSquareWarning, Gauge } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  serviceable: { label: "Serviceable", cls: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500" },
  unserviceable: { label: "Unserviceable", cls: "bg-rose-100 text-rose-800", dot: "bg-rose-500" },
  maintenance: { label: "In Maintenance", cls: "bg-amber-100 text-amber-800", dot: "bg-amber-400" },
};
const todayIso = () => new Date().toLocaleDateString("en-CA");
const isOverdue = (d) => !!d && d < todayIso();
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

function PinField({ value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-gray-500">4-digit PIN *</Label>
      <Input
        className="mt-1 h-9 w-28 font-mono tracking-[0.3em] text-center"
        inputMode="numeric"
        maxLength={4}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
        placeholder="••••"
        data-testid={testid}
      />
    </div>
  );
}

export default function GSEScan() {
  const { eid } = useParams();
  const [unit, setUnit] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState(null); // null | "report" | "status" | "odometer"
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [text, setText] = useState("");
  const [status, setStatus] = useState("serviceable");
  const [note, setNote] = useState("");
  const [odo, setOdo] = useState({ hours: "", km: "", next_due: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/gse/public/${eid}`);
      setUnit(r.data);
      setStatus(r.data.status || "serviceable");
      setOdo({ hours: r.data.odometer_hours ?? "", km: r.data.odometer_km ?? "", next_due: r.data.odometer_next_due || "" });
    } catch {
      setNotFound(true);
    }
  }, [eid]);
  useEffect(() => { load(); }, [load]);

  const validate = () => {
    if (!name.trim()) { toast.error("Your name is required"); return false; }
    if (!/^\d{4}$/.test(pin)) { toast.error("Enter the 4-digit PIN"); return false; }
    return true;
  };

  const submitReport = async () => {
    if (!validate()) return;
    if (!text.trim()) { toast.error("Describe the problem"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/gse/public/${eid}/problems`, { pin, name: name.trim(), text: text.trim() });
      setUnit(r.data);
      setText("");
      setMode(null);
      toast.success("Problem reported — the GSE team has been notified");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit report");
    } finally {
      setBusy(false);
    }
  };

  const submitStatus = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/gse/public/${eid}/status`, { pin, name: name.trim(), status, note: note.trim() });
      setUnit(r.data);
      setNote("");
      setMode(null);
      toast.success(`Status updated to ${STATUS_META[status]?.label || status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setBusy(false);
    }
  };

  const submitOdometer = async () => {
    if (!validate()) return;
    const tracks = unit.odometer_tracks || [];
    const body = { pin, name: name.trim() };
    if (tracks.includes("hours") && odo.hours !== "") body.hours = Number(odo.hours);
    if (tracks.includes("kilometers") && odo.km !== "") body.km = Number(odo.km);
    if (tracks.includes("next_due") && odo.next_due) body.next_due = odo.next_due;
    if (body.hours === undefined && body.km === undefined && !body.next_due) { toast.error("Enter at least one odometer value"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/gse/public/${eid}/odometer`, body);
      setUnit(r.data);
      setOdo({ hours: r.data.odometer_hours ?? "", km: r.data.odometer_km ?? "", next_due: r.data.odometer_next_due || "" });
      setMode(null);
      toast.success("Odometer updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update odometer");
    } finally {
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-summit-canvas flex items-center justify-center px-4">
        <div className="text-center" data-testid="qr-not-found">
          <Truck className="w-8 h-8 mx-auto text-gray-300" />
          <h1 className="font-display text-xl font-semibold mt-3">Equipment not found</h1>
          <p className="text-sm text-gray-500 mt-1">This QR code doesn&apos;t match any registered unit.</p>
        </div>
      </div>
    );
  }
  if (!unit) return <div className="min-h-screen bg-summit-canvas flex items-center justify-center text-sm text-gray-400">Loading…</div>;

  const m = STATUS_META[unit.status] || { label: unit.status, cls: "bg-gray-100 text-gray-600", dot: "bg-gray-400" };
  const problems = unit.problem_reports || [];

  return (
    <div className="min-h-screen bg-summit-canvas px-4 py-6">
      <div className="max-w-md mx-auto">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-2 flex items-center gap-1.5">
          <Truck className="w-3.5 h-3.5" /> Summit Air · Ground Support Equipment
        </div>
        <div className="bg-white border border-summit-hairline rounded-lg overflow-hidden" data-testid="qr-unit-card">
          <div className="bg-summit-primary text-white px-4 py-3 flex items-center gap-2.5 flex-wrap">
            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${m.dot}`} />
            <span className="font-display font-semibold" data-testid="qr-unit-name">{unit.name}</span>
            <span className={`ml-auto px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${m.cls}`} data-testid="qr-status-badge">{m.label}</span>
          </div>
          <div className="p-4 space-y-1 text-sm">
            <div className="flex justify-between gap-2"><span className="text-gray-400 text-xs">Category</span><span>{unit.category || "—"}</span></div>
            <div className="flex justify-between gap-2"><span className="text-gray-400 text-xs">Base</span><span>{unit.base || "—"}</span></div>
            <div className="flex justify-between gap-2"><span className="text-gray-400 text-xs">Serial</span><span>{unit.serial || "—"}</span></div>
            <div className="flex justify-between gap-2"><span className="text-gray-400 text-xs">Part #</span><span>{unit.part_number || "—"}</span></div>
            {unit.has_odometer && (unit.odometer_tracks || []).includes("hours") && (
              <div className="flex justify-between gap-2"><span className="text-gray-400 text-xs">Odometer hours</span><span data-testid="qr-odo-hours">{unit.odometer_hours ?? "—"}</span></div>
            )}
            {unit.has_odometer && (unit.odometer_tracks || []).includes("kilometers") && (
              <div className="flex justify-between gap-2"><span className="text-gray-400 text-xs">Odometer km</span><span data-testid="qr-odo-km">{unit.odometer_km ?? "—"}</span></div>
            )}
            {unit.has_odometer && (unit.odometer_tracks || []).includes("next_due") && (
              <div className="flex justify-between gap-2">
                <span className="text-gray-400 text-xs">Next due</span>
                <span className={isOverdue(unit.odometer_next_due) ? "text-rose-600 font-medium" : ""} data-testid="qr-odo-due">
                  {unit.odometer_next_due || "—"}{isOverdue(unit.odometer_next_due) ? " · OVERDUE" : ""}
                </span>
              </div>
            )}
            {unit.status_changed_by_name && (
              <div className="text-[11px] text-gray-500 pt-1" data-testid="qr-status-stamp">
                {m.label} since {fmtWhen(unit.status_changed_at)} · {unit.status_changed_by_name}
                {unit.status_note ? <span className="italic"> — “{unit.status_note}”</span> : null}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 bg-white border border-summit-hairline rounded-lg p-4" data-testid="qr-problems">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-3 h-3" /> Open problem reports ({problems.length})
          </div>
          {problems.length === 0 && <p className="text-xs text-gray-400">No open problems.</p>}
          <div className="space-y-2">
            {problems.map((p) => (
              <div key={p.id} className="border border-rose-200 rounded-md px-2.5 py-2 bg-rose-50/50 text-xs" data-testid={`qr-pr-${p.id}`}>
                <div className="whitespace-pre-wrap">{p.text}</div>
                <div className="text-[10px] text-gray-500 mt-1">{p.reported_by_name} · {fmtWhen(p.created_at)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button
            variant={mode === "report" ? "default" : "outline"}
            className={mode === "report" ? "bg-summit-primary hover:bg-summit-primary-soft text-white" : ""}
            onClick={() => setMode(mode === "report" ? null : "report")}
            data-testid="qr-report-toggle"
          >
            <MessageSquareWarning className="w-4 h-4 mr-1.5" /> Report a problem
          </Button>
          <Button
            variant={mode === "status" ? "default" : "outline"}
            className={mode === "status" ? "bg-summit-primary hover:bg-summit-primary-soft text-white" : ""}
            onClick={() => setMode(mode === "status" ? null : "status")}
            data-testid="qr-status-toggle"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" /> Update status
          </Button>
          {unit.has_odometer && (
            <Button
              variant={mode === "odometer" ? "default" : "outline"}
              className={`col-span-2 ${mode === "odometer" ? "bg-summit-primary hover:bg-summit-primary-soft text-white" : ""}`}
              onClick={() => setMode(mode === "odometer" ? null : "odometer")}
              data-testid="qr-odometer-toggle"
            >
              <Gauge className="w-4 h-4 mr-1.5" /> Update odometer
            </Button>
          )}
        </div>

        {mode && (
          <div className="mt-3 bg-white border border-summit-hairline rounded-lg p-4 space-y-3" data-testid={`qr-${mode}-form`}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Your name *</Label>
                <Input className="mt-1 h-9" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. J. Smith" maxLength={60} data-testid="qr-name" />
              </div>
              <PinField value={pin} onChange={setPin} testid="qr-pin" />
            </div>
            {mode === "report" ? (
              <>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">What&apos;s wrong? *</Label>
                  <Textarea className="mt-1 text-sm" rows={3} value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Describe the problem…" data-testid="qr-report-text" />
                </div>
                <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submitReport} disabled={busy} data-testid="qr-report-submit">
                  Submit problem report
                </Button>
              </>
            ) : mode === "status" ? (
              <>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">New status *</Label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {Object.entries(STATUS_META).map(([key, sm]) => (
                      <button
                        key={key}
                        className={`px-2.5 py-1.5 rounded-md border text-xs font-medium transition-colors ${status === key ? "border-summit-primary bg-summit-glacier/60" : "border-summit-hairline bg-white hover:bg-summit-glacier/40"}`}
                        onClick={() => setStatus(key)}
                        data-testid={`qr-status-opt-${key}`}
                      >
                        <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${sm.dot}`} />{sm.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">Note (optional)</Label>
                  <Input className="mt-1 h-9" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="e.g. hydraulic leak" data-testid="qr-status-note" />
                </div>
                <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submitStatus} disabled={busy} data-testid="qr-status-submit">
                  Update status
                </Button>
              </>
            ) : (
              <>
                {(unit.odometer_tracks || []).includes("hours") && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-gray-500">Odometer hours</Label>
                    <Input className="mt-1 h-9" type="number" min="0" step="0.1" value={odo.hours} onChange={(e) => setOdo((o) => ({ ...o, hours: e.target.value }))} data-testid="qr-odo-input-hours" />
                  </div>
                )}
                {(unit.odometer_tracks || []).includes("kilometers") && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-gray-500">Odometer kilometers</Label>
                    <Input className="mt-1 h-9" type="number" min="0" step="1" value={odo.km} onChange={(e) => setOdo((o) => ({ ...o, km: e.target.value }))} data-testid="qr-odo-input-km" />
                  </div>
                )}
                {(unit.odometer_tracks || []).includes("next_due") && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-gray-500">Next due date</Label>
                    <Input className="mt-1 h-9" type="date" value={odo.next_due} onChange={(e) => setOdo((o) => ({ ...o, next_due: e.target.value }))} data-testid="qr-odo-input-due" />
                  </div>
                )}
                <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submitOdometer} disabled={busy} data-testid="qr-odometer-submit">
                  Update odometer
                </Button>
              </>
            )}
            <p className="text-[10px] text-gray-400">Changes are stamped with your name, date &amp; time. PIN is provided by the GSE team.</p>
          </div>
        )}
      </div>
    </div>
  );
}
